import { create } from 'zustand';
import type { Listing, ListingsParams, ListingsResponse, MapListing, LiveSortBy } from '../services/api';
import { api, ApiError } from '../services/api';

// User-friendly error messages
const LISTINGS_ERROR_MESSAGES = {
  fetch: 'Impossibile caricare gli annunci. Riprova piu tardi.',
  map: 'Impossibile caricare gli annunci sulla mappa. Riprova piu tardi.',
  network: 'Verifica la connessione internet e riprova.',
};

// Property condition type
type PropertyCondition = 'nuovo' | 'ristrutturato' | 'buono' | 'da_ristrutturare';

// Extended filters type with live sort
interface ExtendedFilters extends ListingsParams {
  liveSortBy?: LiveSortBy;
  hasElevator?: boolean;
  hasTerrace?: boolean;
  isFurnished?: boolean;
  hasGarden?: boolean;
  propertyCondition?: PropertyCondition;
}

// SessionStorage key for filter persistence
const FILTERS_STORAGE_KEY = 'homefinder_filters';

// Keys to persist (exclude transient state like page)
const PERSISTABLE_KEYS = [
  'city', 'contractType', 'propertyType', 'minPrice', 'maxPrice',
  'minSurface', 'maxSurface', 'minRooms', 'maxRooms', 'minBathrooms',
  'liveSortBy', 'hasElevator', 'hasTerrace', 'isFurnished', 'hasGarden',
  'propertyCondition',
];

function saveFiltersToSession(filters: ExtendedFilters) {
  try {
    const toSave: Record<string, any> = {};
    for (const key of PERSISTABLE_KEYS) {
      const val = (filters as any)[key];
      if (val !== undefined && val !== null) {
        toSave[key] = val;
      }
    }
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toSave));
  } catch { /* ignore quota errors */ }
}

function loadFiltersFromSession(): Partial<ExtendedFilters> {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

interface ListingsState {
  listings: Listing[];
  mapListings: MapListing[];
  listingsCache: Map<string, Listing>; // Cache listings by ID for detail view
  pagination: ListingsResponse['pagination'] | null;
  filters: ExtendedFilters;
  isLoading: boolean;
  error: string | null;
  selectedListingId: string | null;

  setFilters: (filters: Partial<ExtendedFilters>, resetPage?: boolean) => void;
  resetFilters: (keepCity?: boolean) => void;
  fetchListings: (append?: boolean) => Promise<void>;
  fetchMapListings: (bounds: { north: number; south: number; east: number; west: number }) => Promise<void>;
  setSelectedListing: (id: string | null) => void;
  toggleFavorite: (listingId: string, isFavorite: boolean) => void;
  clearError: () => void;
  getListingById: (id: string) => Listing | null;
}

const defaultFilters: ExtendedFilters = {
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  city: 'Milano', // Default city for live search
  liveSortBy: 'date', // Default sort by most recent for live API
};

// Merge saved filters with defaults on startup
const savedFilters = loadFiltersFromSession();
const initialFilters: ExtendedFilters = { ...defaultFilters, ...savedFilters };

export const useListingsStore = create<ListingsState>((set, get) => ({
  listings: [],
  mapListings: [],
  listingsCache: new Map(),
  pagination: null,
  filters: initialFilters,
  isLoading: false,
  error: null,
  selectedListingId: null,

  setFilters: (newFilters, resetPage = true) => {
    set((state) => {
      const updated = {
        ...state.filters,
        ...newFilters,
        page: resetPage && !('page' in newFilters) ? 1 : (newFilters.page ?? state.filters.page ?? 1),
      };
      saveFiltersToSession(updated);
      return { filters: updated, error: null };
    });
  },

  resetFilters: (keepCity = true) => {
    const resetTo = keepCity ? defaultFilters : { ...defaultFilters, city: undefined };
    saveFiltersToSession(resetTo);
    set({
      filters: resetTo,
      listings: [],
      pagination: null,
      error: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  fetchListings: async (append = false) => {
    const { filters } = get();
    set({ isLoading: true, error: null });

    try {
      let response;

      // Always use live API from Immobiliare.it
      // Default to Milano if no city specified
      const searchCity = filters.city?.trim() || 'Milano';

      console.log('[Listings] Fetching from live API:', {
        city: searchCity,
        contractType: filters.contractType,
        sortBy: filters.liveSortBy || 'date',
        filters: filters,
      });

      response = await api.getLiveListings({
        city: searchCity,
        contractType: filters.contractType,
        page: filters.page,
        limit: filters.limit,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minSurface: filters.minSurface,
        maxSurface: filters.maxSurface,
        minRooms: filters.minRooms,
        maxRooms: filters.maxRooms,
        bathrooms: filters.minBathrooms,
        sortBy: filters.liveSortBy || 'date',
        // Feature filters
        hasElevator: filters.hasElevator,
        hasTerrace: filters.hasTerrace,
        isFurnished: filters.isFurnished,
        hasGarden: filters.hasGarden,
        // Property condition (for sales)
        propertyCondition: filters.propertyCondition,
      });

      // Deduplicate listings by ID
      const currentListings = get().listings;
      const newListings = append
        ? [...currentListings, ...response.data]
        : response.data;

      const uniqueListings = Array.from(
        new Map(newListings.map(item => [item.id, item])).values()
      );

      // Update cache with new listings
      const cache = new Map(get().listingsCache);
      uniqueListings.forEach(listing => {
        cache.set(listing.id, listing);
      });

      set({
        listings: uniqueListings,
        listingsCache: cache,
        pagination: response.pagination,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      let errorMessage = LISTINGS_ERROR_MESSAGES.fetch;

      if (error instanceof ApiError) {
        // Use the user-friendly message from ApiError
        errorMessage = error.message;

        // Add network-specific hint if applicable
        if (error.status === 0) {
          errorMessage = LISTINGS_ERROR_MESSAGES.network;
        }
      }

      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  fetchMapListings: async (bounds) => {
    const { filters } = get();
    set({ isLoading: true, error: null });

    try {
      console.log('[Map] Fetching via live/map endpoint:', {
        bounds,
        filters: {
          contractType: filters.contractType,
          minPrice: filters.minPrice,
          maxPrice: filters.maxPrice,
          minRooms: filters.minRooms,
          maxRooms: filters.maxRooms,
        },
      });

      // Use the new viewport-based endpoint that queries both APIs with proper bounds
      const response = await api.getLiveMapListings({
        north: bounds.north,
        south: bounds.south,
        east: bounds.east,
        west: bounds.west,
        contractType: filters.contractType,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minSurface: filters.minSurface,
        maxSurface: filters.maxSurface,
        minRooms: filters.minRooms,
        maxRooms: filters.maxRooms,
        bathrooms: filters.minBathrooms,
        sortBy: filters.liveSortBy || 'date',
        hasElevator: filters.hasElevator,
        hasTerrace: filters.hasTerrace,
        isFurnished: filters.isFurnished,
        hasGarden: filters.hasGarden,
        propertyCondition: filters.propertyCondition,
      });

      const allListings = response.data;

      // Update listings cache
      const cache = new Map(get().listingsCache);
      allListings.forEach(listing => {
        cache.set(listing.id, listing);
      });

      // Update main listings with results (for list view consistency)
      if (get().listings.length === 0) {
        set({ listings: allListings.slice(0, 20) });
      }

      set({ listingsCache: cache });

      // Convert to map format (already filtered by bounds on server)
      const mapListings = allListings
        .filter(listing => listing.latitude && listing.longitude)
        .map(listing => {
          // Extract first image URL
          let imageUrl: string | undefined;
          if (listing.imageUrls && listing.imageUrls.length > 0) {
            imageUrl = listing.imageUrls[0];
          } else if (listing.images && listing.images.length > 0) {
            const first = listing.images[0];
            imageUrl = typeof first === 'string' ? first : first.url;
          }
          return {
            id: listing.id,
            latitude: listing.latitude!,
            longitude: listing.longitude!,
            price: listing.price || null,
            contractType: listing.contractType,
            propertyType: listing.propertyType,
            title: listing.title,
            imageUrl,
            surface: listing.surface || undefined,
            rooms: listing.rooms || undefined,
            address: listing.address || undefined,
          };
        });

      console.log(`[Map] Loaded ${mapListings.length} listings from both APIs`);

      set({
        mapListings,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      let errorMessage = LISTINGS_ERROR_MESSAGES.map;

      if (error instanceof ApiError) {
        errorMessage = error.message;

        if (error.status === 0) {
          errorMessage = LISTINGS_ERROR_MESSAGES.network;
        }
      }

      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setSelectedListing: (id) => {
    set({ selectedListingId: id });
  },

  toggleFavorite: (listingId, isFavorite) => {
    set((state) => ({
      listings: state.listings.map((listing) =>
        listing.id === listingId ? { ...listing, isFavorite } : listing
      ),
    }));
  },

  getListingById: (id) => {
    return get().listingsCache.get(id) || null;
  },
}));
